import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Check, X, Sparkles, Plus, FileText, Loader2, Play } from "lucide-react";
import { PageContainer, PageHeader, Card, Button, Input, Loading } from "../components/kit";

const ROUNDS = ["hr", "technical", "behavioral"];
const ROUND_LABELS = { hr: "HR", technical: "Technical", behavioral: "Behavioral" };
const ROUND_COLORS = {
  hr: "bg-[#0025D8] text-white",
  technical: "bg-[#0F172A] text-white",
  behavioral: "bg-[#7C3AED] text-white",
};
const MAX_DOCS = 5;
const MAX_SIZE_MB = 10;
const MAX_SELECTED = 5;

function RoundTab({ round, active, onClick, selectedCount }) {
  return (
    <button onClick={onClick}
      className={`px-5 py-3 font-mono text-xs uppercase tracking-wider font-semibold border-b-2 transition-colors ${
        active ? "border-[#0025D8] text-[#0025D8]" : "border-transparent text-muted-foreground hover:text-foreground"
      }`}>
      {ROUND_LABELS[round]}
      <span className="ml-2 font-normal">({selectedCount} selected)</span>
    </button>
  );
}

function FileStatusRow({ name, status, error }) {
  return (
    <div className="flex items-center gap-2 text-xs py-1">
      <span className="flex-shrink-0">
        {status === "uploading" && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0025D8]" />}
        {status === "done"      && <Check   className="w-3.5 h-3.5 text-[#10B981]" />}
        {status === "failed"    && <X       className="w-3.5 h-3.5 text-[#E11D48]" />}
        {status === "pending"   && <span className="w-3.5 h-3.5 inline-block rounded-full border border-muted-foreground/40" />}
      </span>
      <span className={`truncate flex-1 ${status === "failed" ? "text-[#E11D48]" : ""}`}>{name}</span>
      {error && <span className="text-[#E11D48] text-[10px] flex-shrink-0">{error}</span>}
    </div>
  );
}

export default function CustomRoleDetail() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRound, setActiveRound] = useState("hr");
  const [uploading, setUploading] = useState(false);
  const [fileStatuses, setFileStatuses] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [addingManual, setAddingManual] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [starting, setStarting] = useState(false);
  const fileRef = useRef(null);

  const fetchRole = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/candidate/roles/${roleId}`);
      setRole(data);
    } catch {
      toast.error("Could not load role");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRole(); }, [roleId]); // eslint-disable-line

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    const docs = role?.documents?.[activeRound] || [];
    const slots = MAX_DOCS - docs.length;
    let slotsUsed = 0;
    const statuses = files.map((f) => {
      if (f.size > MAX_SIZE_MB * 1024 * 1024)
        return { name: f.name, status: "failed", error: `> ${MAX_SIZE_MB} MB`, file: null };
      if (slotsUsed >= slots)
        return { name: f.name, status: "failed", error: `${MAX_DOCS}-doc limit`, file: null };
      slotsUsed++;
      return { name: f.name, status: "pending", error: null, file: f };
    });
    setFileStatuses(statuses);
    setUploading(true);
    let succeeded = 0;
    let failed = statuses.filter((s) => s.status === "failed").length;
    for (let i = 0; i < statuses.length; i++) {
      if (statuses[i].status === "failed") continue;
      setFileStatuses((prev) => prev.map((s, idx) => idx === i ? { ...s, status: "uploading" } : s));
      try {
        const form = new FormData();
        form.append("file", statuses[i].file);
        await api.post(`/candidate/roles/${roleId}/documents/${activeRound}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFileStatuses((prev) => prev.map((s, idx) => idx === i ? { ...s, status: "done" } : s));
        succeeded++;
      } catch (err) {
        const msg = err?.response?.data?.detail || "Upload failed";
        setFileStatuses((prev) => prev.map((s, idx) => idx === i ? { ...s, status: "failed", error: msg } : s));
        failed++;
      }
    }
    await fetchRole();
    setUploading(false);
    if (failed === 0) toast.success(`${succeeded} file${succeeded !== 1 ? "s" : ""} uploaded`);
    else if (succeeded === 0) toast.error(`All ${failed} file${failed !== 1 ? "s" : ""} failed`);
    else toast.warning(`${succeeded} uploaded, ${failed} failed`);
  };

  const deleteDoc = async (docId) => {
    try {
      await api.delete(`/candidate/roles/${roleId}/documents/${activeRound}/${docId}`);
      toast.success("Document removed");
      await fetchRole();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const getSuggestions = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/candidate/roles/${roleId}/questions/${activeRound}/suggest`);
      toast.success(`${data.added} AI suggestions added — select up to ${MAX_SELECTED} below`);
      await fetchRole();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate suggestions");
    } finally { setSuggesting(false); }
  };

  const addManualQuestion = async () => {
    if (!newQuestion.trim()) return;
    setAddingManual(true);
    try {
      await api.post(`/candidate/roles/${roleId}/questions/${activeRound}`, { text: newQuestion.trim() });
      setNewQuestion("");
      await fetchRole();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add question");
    } finally { setAddingManual(false); }
  };

  const toggleSelected = async (q) => {
    try {
      await api.patch(`/candidate/roles/${roleId}/questions/${activeRound}/${q.q_id}`, { selected: !q.selected });
      setRole((r) => {
        const qs = r.questions[activeRound].map((x) => x.q_id === q.q_id ? { ...x, selected: !q.selected } : x);
        return { ...r, questions: { ...r.questions, [activeRound]: qs } };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update question");
    }
  };

  const saveEdit = async (q) => {
    if (!editText.trim()) return;
    try {
      await api.patch(`/candidate/roles/${roleId}/questions/${activeRound}/${q.q_id}`, { text: editText.trim() });
      setRole((r) => {
        const qs = r.questions[activeRound].map((x) => x.q_id === q.q_id ? { ...x, text: editText.trim() } : x);
        return { ...r, questions: { ...r.questions, [activeRound]: qs } };
      });
      setEditingId(null);
      toast.success("Question updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const startInterview = async () => {
    if (readyRounds.length === 0) return;
    setStarting(true);
    try {
      const { data } = await api.post("/interviews", {
        source: "custom_role",
        candidate_role_id: roleId,
        rounds: readyRounds,
      });
      navigate(`/interview/${data.interview_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start interview");
      setStarting(false);
    }
  };

  const deleteQuestion = async (q) => {
    try {
      await api.delete(`/candidate/roles/${roleId}/questions/${activeRound}/${q.q_id}`);
      setRole((r) => {
        const qs = r.questions[activeRound].filter((x) => x.q_id !== q.q_id);
        return { ...r, questions: { ...r.questions, [activeRound]: qs } };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  if (loading) return <Loading />;
  if (!role) return <Loading label="Role not found." />;

  const docs = role.documents?.[activeRound] || [];
  const questions = role.questions?.[activeRound] || [];
  const selectedCount = questions.filter((q) => q.selected).length;
  const atDocLimit = docs.length >= MAX_DOCS;
  const atSelectLimit = selectedCount >= MAX_SELECTED;
  const readyRounds = ROUNDS.filter((rnd) => (role.questions?.[rnd] || []).some((q) => q.selected));

  return (
    <PageContainer width="lg">
      <PageHeader
        back={{ to: "/custom-roles", label: "Custom Interviews" }}
        title={role.name}
        subtitle={readyRounds.length > 0
          ? `Ready rounds: ${readyRounds.map((r) => ROUND_LABELS[r]).join(", ")} — select rounds on the next screen`
          : undefined}
        actions={
          <Button onClick={startInterview} disabled={readyRounds.length === 0 || starting} loading={starting}>
            {!starting && <Play className="w-4 h-4" />}
            {starting ? "Starting…" : readyRounds.length === 0 ? "Select questions to begin" : "Start Interview"}
          </Button>
        }
      />

      {/* Round tabs */}
      <div className="flex border-b border-border">
        {ROUNDS.map((rnd) => (
          <RoundTab key={rnd} round={rnd} active={activeRound === rnd}
            onClick={() => { setActiveRound(rnd); setFileStatuses(null); }}
            selectedCount={(role.questions?.[rnd] || []).filter((q) => q.selected).length} />
        ))}
      </div>

      {/* Setup progress strip */}
      <div className="flex rounded-xl border border-border overflow-hidden mt-4 mb-8 divide-x divide-border">
        {[
          { step: "01", label: "Upload documents", done: docs.length > 0 },
          { step: "02", label: "Generate questions", done: questions.length > 0 },
          { step: "03", label: "Select questions", done: selectedCount > 0 },
          { step: "04", label: "Start interview", done: readyRounds.length > 0 },
        ].map((s) => (
          <div key={s.step} className={`flex-1 px-3 py-2.5 flex items-center gap-2 ${s.done ? "bg-[#10B981]/8" : "bg-white"}`}>
            <div className={`w-5 h-5 rounded-md flex items-center justify-center font-mono text-[10px] font-bold flex-shrink-0 ${s.done ? "bg-[#10B981] text-white" : "bg-secondary text-muted-foreground border border-border"}`}>
              {s.done ? "✓" : s.step}
            </div>
            <span className={`text-[11px] leading-tight ${s.done ? "text-[#10B981] font-semibold" : "text-muted-foreground"}`}>{s.label}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Documents */}
        <div className="lg:col-span-1">
          <Card padding="p-0" className="overflow-hidden">
            <div className={`px-4 py-3 ${ROUND_COLORS[activeRound]}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em]">{ROUND_LABELS[activeRound]} Round</div>
              <div className="font-semibold text-sm mt-0.5">Reference Documents</div>
            </div>
            <div className="p-4 space-y-3">
              {docs.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-4 text-center">
                  <Upload className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs font-semibold text-muted-foreground">No documents yet</p>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">Upload a JD, resume, or any reference file to unlock AI question generation.</p>
                </div>
              ) : (
                docs.map((doc) => (
                  <div key={doc.doc_id} className="flex items-start justify-between gap-2 rounded-xl border border-border p-3">
                    <div className="flex items-start gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-[#0F172A] truncate">{doc.filename}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">{doc.size ? `${Math.round(doc.size / 1024)} KB` : ""}</div>
                      </div>
                    </div>
                    <button onClick={() => deleteDoc(doc.doc_id)} className="text-muted-foreground hover:text-[#E11D48] flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}

              {fileStatuses && (
                <div className="rounded-xl border border-border p-3 bg-secondary/30 space-y-0.5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Upload progress</div>
                  {fileStatuses.map((s, i) => <FileStatusRow key={i} name={s.name} status={s.status} error={s.error} />)}
                </div>
              )}

              <div className="font-mono text-[10px] text-muted-foreground">{docs.length} / {MAX_DOCS} documents · max {MAX_SIZE_MB} MB each</div>

              <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.md" className="hidden" onChange={uploadFiles} />
              <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={uploading || atDocLimit} className="w-full">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? "Uploading…" : atDocLimit ? `Limit reached (${MAX_DOCS}/${MAX_DOCS})` : `Upload documents (${docs.length}/${MAX_DOCS})`}
              </Button>
              <p className="text-[10px] text-muted-foreground">PDF, DOCX, TXT or MD · up to {MAX_DOCS} files.</p>
            </div>
          </Card>
        </div>

        {/* Right: Questions */}
        <div className="lg:col-span-2 space-y-4">
          {/* AI Suggestions */}
          <Card padding="p-4" className="bg-[#0025D8]/5 border-[#0025D8]/30">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0025D8]">AI Question Suggestions</div>
                <p className="text-xs text-muted-foreground mt-0.5">AI generates up to 10 questions from your uploaded documents. Select up to {MAX_SELECTED} per round.</p>
              </div>
              <Button onClick={getSuggestions} disabled={suggesting || docs.length === 0} className="flex-shrink-0">
                <Sparkles className={`w-3.5 h-3.5 ${suggesting ? "animate-pulse" : ""}`} />
                {suggesting ? "Generating…" : "AI Question Suggestions"}
              </Button>
            </div>
          </Card>

          {/* Add manual question */}
          <Card padding="p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Add question manually</div>
            <div className="flex gap-2">
              <Input value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addManualQuestion(); }}
                placeholder="Type your question…" className="flex-1 py-2.5" />
              <Button variant="secondary" onClick={addManualQuestion} disabled={addingManual || !newQuestion.trim()} className="px-3">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </Card>

          {/* Question list */}
          <Card padding="p-0" className="overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Questions</div>
                <div className="text-sm font-semibold mt-0.5 text-[#0F172A]">
                  {selectedCount} / {MAX_SELECTED} selected
                  <span className="text-muted-foreground font-normal"> — {questions.length} total</span>
                </div>
              </div>
              {atSelectLimit ? (
                <span className="font-mono text-[10px] text-[#F59E0B]">Max {MAX_SELECTED} selected</span>
              ) : questions.length > 0 && selectedCount === 0 ? (
                <span className="font-mono text-[10px] text-[#F59E0B]">Select at least 1 to start</span>
              ) : null}
            </div>

            {questions.length === 0 ? (
              <div className="p-8 text-center">
                {docs.length === 0 ? (
                  <>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] mb-2">STEP 1 NEEDED</div>
                    <p className="text-sm font-semibold text-muted-foreground">Upload a document first</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">Add at least one reference file on the left, then click "AI Question Suggestions" to auto-generate questions — or type questions manually below.</p>
                  </>
                ) : (
                  <>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0025D8] mb-2">STEP 2 NEEDED</div>
                    <p className="text-sm font-semibold text-muted-foreground">No questions yet</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">Click "AI Question Suggestions" above to generate questions from your documents, or add your own manually.</p>
                  </>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
                {questions.map((q, idx) => (
                  <div key={q.q_id} className={`p-4 flex gap-3 items-start ${q.selected ? "" : "opacity-60"}`}>
                    <span className="font-mono text-[10px] text-muted-foreground pt-1 w-7 flex-shrink-0">{String(idx + 1).padStart(2, "0")}</span>
                    <div className="flex-1 min-w-0">
                      {editingId === q.q_id ? (
                        <div className="flex gap-2">
                          <Input autoFocus value={editText} onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(q); if (e.key === "Escape") setEditingId(null); }}
                            className="py-2 flex-1" />
                          <Button size="sm" variant="secondary" className="px-2" onClick={() => saveEdit(q)}><Check className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="secondary" className="px-2" onClick={() => setEditingId(null)}><X className="w-3.5 h-3.5" /></Button>
                        </div>
                      ) : (
                        <p className="text-sm leading-relaxed text-[#0F172A]">{q.text}</p>
                      )}
                      <span className={`font-mono text-[10px] ${q.source === "ai" ? "text-[#0025D8]" : "text-muted-foreground"}`}>
                        {q.source === "ai" ? "AI suggestion" : "Manual"}
                      </span>
                    </div>
                    {editingId !== q.q_id && (
                      <div className="flex gap-1 flex-shrink-0 items-center">
                        <button onClick={() => { setEditingId(q.q_id); setEditText(q.text); }}
                          className="p-1.5 text-muted-foreground hover:text-foreground text-[10px] font-mono" title="Edit">Edit</button>
                        <button onClick={() => toggleSelected(q)} disabled={!q.selected && atSelectLimit}
                          className={`p-1.5 text-xs font-mono font-semibold transition-colors ${
                            q.selected ? "text-[#10B981] hover:text-[#E11D48]"
                            : atSelectLimit ? "text-muted-foreground cursor-not-allowed" : "text-muted-foreground hover:text-[#10B981]"
                          }`}
                          title={q.selected ? "Deselect" : "Select for interview"}>
                          {q.selected ? "✓ Selected" : "Select"}
                        </button>
                        <button onClick={() => deleteQuestion(q)} className="p-1.5 text-muted-foreground hover:text-[#E11D48]" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </PageContainer>
  );
}
